import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  UsersIcon, 
  MapPinIcon, 
  BoltIcon as LightningBoltIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

const About = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  const features = [
    {
      icon: ChartBarIcon,
      title: "Auto-Sync with Planning Center",
      subtitle: "Real-time database updates",
      description: "Every conversation, every new member, every update flows seamlessly into your Planning Center database. No manual data entry. Ever.",
      benefits: [
        "Automatic profile creation for new members",
        "Real-time contact information updates", 
        "Zero manual data entry required",
        "Instant group membership tracking"
      ],
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: UsersIcon,
      title: "Effortless Group Management",
      subtitle: "Connect people instantly",
      description: "Share group links, coordinate meetings, and build community with tools designed specifically for small group leaders.",
      benefits: [
        "One-click group link sharing",
        "Integrated scheduling and location sharing",
        "Direct messaging with group leaders",
        "Automated group announcements"
      ],
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: MapPinIcon,
      title: "Interactive Groups Map",
      subtitle: "Find community on the map",
      description: "Your members can discover and join small groups through an intuitive, real-time map interface that makes connection effortless.",
      benefits: [
        "Live map with group locations",
        "View church information and events",
        "Direct group joining from map",
        "Church website integration"
      ],
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: ShieldCheckIcon,
      title: "Keep Your Community Safe",
      subtitle: "Explicit content filtering & moderation with AI",
      description: "We leverage AI to filter explicit content and ensure your church community remains a safe space for all members.",
      benefits: [
        "Automated explicit content detection",
        "Real-time message moderation",
        "Safe environment for all ages",
        "Advanced safety reporting tools"
      ],
      gradient: "from-red-500 to-orange-500"
    }
  ];

  const stats = [
    { number: "100%", label: "Planning Center Integration", icon: CircleStackIcon },
    { number: "0%", label: "Manual Data Entry", icon: CheckCircleIcon },
    { number: "∞", label: "Growth Potential", icon: SparklesIcon }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div id="about" className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 animate-pulse"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-bounce"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-bounce delay-1000"></div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-20">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
                        <motion.div
              className="relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium mb-8 overflow-hidden"
              style={{ background: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                delay: 0.2, 
                type: "spring", 
                stiffness: 260, 
                damping: 20 
              }}
              whileHover={{ scale: 1.05, y: -2 }}
            >
              {/* Animated background shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{
                  x: [-100, 200],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 3,
                }}
              />
              
              <motion.div
                animate={{ rotate: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <LightningBoltIcon className="w-4 h-4 text-white" />
              </motion.div>
              
              <span className="relative z-10 text-white font-semibold">
                Welcome to the Chat
              </span>
              
              <motion.div
                animate={{ rotate: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              >
                <LightningBoltIcon className="w-4 h-4 text-white" />
              </motion.div>
              
              {/* Pulsing border effect */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-white/30"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
            Why <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>JoltChat?</span>
            </motion.h1>

            <motion.p 
              className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              The <strong className="text-white">only messaging platform</strong> designed exclusively for churches that 
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold"> automatically syncs with Planning Center</span>. 
              Transform how your small groups connect, grow, and thrive.
            </motion.p>

            {/* Stats Row */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 text-center hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  <stat.icon className="w-8 h-8 mx-auto mb-3 text-blue-400" />
                  <div className={`font-bold text-white mb-2 ${index === 2 ? 'text-5xl' : 'text-3xl'}`}>{stat.number}</div>
                  <div className="text-gray-300">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      
      {/* Centered JoltChat Main Image */}
      <motion.div
        className="flex justify-center mb-16 relative"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <motion.img
          src="/assets/images/joltchat-group-details-fwd.png"
          alt="JoltChat Main Interface"
          className="max-w-xl w-full h-auto relative z-10 drop-shadow-2xl"
          whileHover={{ scale: 1.02, y: -5 }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>

      {/* Interactive Features Section */}
      <section ref={ref} className="relative z-10 pb-32">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Revolutionary <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>Features</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Every feature designed to eliminate friction and amplify connection in your church community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Feature Navigation */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-500 ${
                    activeFeature === index 
                      ? 'bg-gradient-to-r from-white/20 to-white/10 border-2 border-white/30' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                  onClick={() => setActiveFeature(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${feature.gradient}`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-400 mb-3">{feature.subtitle}</p>
                      <motion.div
                        initial={false}
                        animate={{ 
                          height: activeFeature === index ? 'auto' : 0,
                          opacity: activeFeature === index ? 1 : 0 
                        }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="text-gray-300 mb-4">{feature.description}</p>
                        <ul className="space-y-2">
                          {feature.benefits.map((benefit, bIndex) => (
                            <motion.li
                              key={bIndex}
                              className="flex items-center gap-2 text-sm text-gray-400"
                              initial={{ opacity: 0, x: -20 }}
                              animate={activeFeature === index ? { opacity: 1, x: 0 } : {}}
                              transition={{ delay: bIndex * 0.1 }}
                            >
                              <CheckCircleIcon className="w-4 h-4 text-green-400" />
                              {benefit}
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    </div>
                  </div>
                  {activeFeature === index && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl"
                      layoutId="activeFeature"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* Feature Visualization */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/20 rounded-3xl p-8 h-96">
                <motion.div
                  key={activeFeature}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="h-full flex flex-col items-center justify-center text-center"
                >
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${features[activeFeature].gradient} flex items-center justify-center mb-6`}>
                    {React.createElement(features[activeFeature].icon, { className: "w-10 h-10 text-white" })}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{features[activeFeature].title}</h3>
                  <p className="text-gray-300 text-lg leading-relaxed">{features[activeFeature].description}</p>
                </motion.div>
                
                {/* Animated particles */}
                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                  {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-blue-400 rounded-full"
                    style={{
                    left: `${20 + i * 15}%`,
                    top: `${20 + (i % 2) * 60}%`,
                    }}
                    animate={{
                    y: [-10, 10, -10],
                    opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                    duration: 2,
                    delay: i * 0.2,
                    repeat: Infinity,
                    }}
                  />
                  ))}
                </div>
                </div>
              </motion.div>
              </div>
            </div>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Link 
              to="/features" 
              className="group relative px-8 py-4 text-white font-medium text-lg rounded-2xl border-2 border-white/20 bg-white/5 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
              >
              <span className="flex items-center gap-2">
                See all Features
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              </Link>
            </motion.div>
            </section>

            <h2 className="center text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"></h2>
      <h2 className="center text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          Everybody wants to join the <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>Chat</span>
          <br /><div className="py-3"></div>
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>Nobody</span> wants to fill out a form
      </h2>

            

      {/* CTA Section */}
      <section className="relative z-10 py-32">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg border border-white/20 rounded-3xl p-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Ready to Transform Your Church Community?
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-300 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
            >
              Join churches already experiencing the JoltChat difference. Setup takes less than 5 minutes.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Link 
  to="/sign-in" 
  className="group relative px-8 py-4 text-white font-bold text-lg rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
  style={{ background: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}
>
                <span className="flex items-center gap-2">
                  Start Today
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;